import { createSelector } from '@reduxjs/toolkit'
import _find from 'lodash/find'
import _orderBy from 'lodash/orderBy'

import { featureFlags } from '@/feature-flags'

import {
    ADDRESS_TYPE_BUSINESS,
    ADDRESS_TYPE_HOME,
    ADDRESS_TYPE_HOME_AND_BUSINESS,
    FORAL_AUTONOMOUS_REGIONS_IDS,
    FORAL_AUTONOMOUS_REGIONS_IDS_WITHOUT_CANARY,
    PLACE_OF_BUSINESS_BUSINESS,
    PLACE_OF_BUSINESS_HOME,
} from '../../constants'
import { getAutonomousRegionNameById } from '../../selectors/autonomous-regions'
import { getCountryNameById, getCountrySpainId } from '../../selectors/countries'

const getAddressesSlice = (state) => state.addresses
const getAddresses = (state) => getAddressesSlice(state).items || []

const getAddressesOrderedByMovedInDateAndId = createSelector([getAddresses], (addresses) =>
    _orderBy(addresses, ['moveInDate', 'id'], ['desc', 'desc'])
)

const getHomeAddress = createSelector([(state) => state, getAddressesOrderedByMovedInDateAndId], (state, addresses) => {
    const address = _find(
        addresses,
        (o) => o.addressType === ADDRESS_TYPE_HOME || o.addressType === ADDRESS_TYPE_HOME_AND_BUSINESS
    )

    /* @TODO: Needs to be undefined for now, ProfilePersonalDetails will blow up if returns null */
    if (!address) return undefined

    return {
        ...address,
        autonomousRegionName: getAutonomousRegionNameById(state, { id: address.autonomousRegionId }),
        countryName: getCountryNameById(state, { id: address.countryId }),
        isForalAutonomousRegion: getIsForalAutonomousRegion(address.autonomousRegionId),
    }
})

const getBusinessAddress = createSelector(
    [(state) => state, getAddressesOrderedByMovedInDateAndId],
    (state, addresses) => {
        const address = _find(addresses, (o) => o.addressType === ADDRESS_TYPE_BUSINESS)

        /* @TODO: Needs to be undefined for now, ProfilePersonalDetails will blow up if returns null */
        if (!address) return undefined

        return {
            ...address,
            autonomousRegionName: getAutonomousRegionNameById(state, { id: address.autonomousRegionId }),
            countryName: getCountryNameById(state, { id: address.countryId }),
            isForalAutonomousRegion: getIsForalAutonomousRegion(address.autonomousRegionId),
        }
    }
)

export const getIsHomeAddressFilled = createSelector([getHomeAddress, getCountrySpainId], (address, countrySpainId) => {
    if (!address) return false

    const isAddressInvalid =
        !address.countryId ||
        (address.countryId === countrySpainId && !address.autonomousRegionId) ||
        !address.city ||
        !address.streetAddress ||
        !address.postcode

    return !isAddressInvalid
})

/*
    @TODO:
        * Keep countrySpainId as a separate selector, do not bundle with addresses
        * Move foral region flag to specific address object
        * Move getIsForalAutonomousRegion to autonomous regions selector
 */
export default createSelector(
    [getAddressesSlice, getHomeAddress, getBusinessAddress, getCountrySpainId],
    (addressesSlice, address, businessAddress, countrySpainId) => {
        const isFromForalAutonomousRegion = address?.isForalAutonomousRegion || businessAddress?.isForalAutonomousRegion

        return {
            ...addressesSlice,
            address,
            businessAddress,
            countrySpainId,
            isAddressFilled: isAddressFilled(address, businessAddress, countrySpainId),
            isFromForalAutonomousRegion,
        }
    }
)

/* @TODO: Rework for both self-employed and annual return customers - currently too specific */
const isAddressFilled = (
    address,
    businessAddress,
    countrySpainId,
    isBusinessAddressRequired = false,
    isAddressSizeRequired = false
) => {
    if (!address) return false

    const {
        autonomousRegionId: addressAutonomousRegionId,
        city: addressCity,
        countryId: addressCountryId,
        postcode: addressPostcode,
        streetAddress: addressStreetAddress,
        addressSizeSquareMeters,
        addressUsagePercentageRange,
    } = address

    const isAddressSpain = Number(addressCountryId) === countrySpainId

    if (isAddressSpain && !addressAutonomousRegionId) return false
    if (!addressCity) return false
    if (!addressCountryId) return false
    if (!addressPostcode) return false
    if (!addressStreetAddress) return false
    if (isAddressSizeRequired && !addressSizeSquareMeters) return false
    if (isAddressSizeRequired && !addressUsagePercentageRange) return false

    if (isBusinessAddressRequired) {
        if (!businessAddress) return false

        const {
            autonomousRegionId: businessAddressAutonomousRegionId,
            city: businessAddressCity,
            countryId: businessAddressCountryId,
            addressOwnership: businessAddressOwnership,
            postcode: businessAddressPostcode,
            streetAddress: businessAddressStreetAddress,
            addressSizeSquareMeters: businessAddressSizeSquareMeters,
        } = businessAddress

        const isBusinessAddressSpain = Number(businessAddressCountryId) === countrySpainId

        if (isBusinessAddressSpain && !businessAddressAutonomousRegionId) return false
        if (!businessAddressCity) return false
        if (!businessAddressCountryId) return false
        if (!businessAddressOwnership) return false
        if (!businessAddressPostcode) return false
        if (!businessAddressStreetAddress) return false
        if (!businessAddressSizeSquareMeters) return false
    }

    return true
}

export const getInvalidAddressFields = (
    address,
    businessAddress,
    { countrySpainId, isSelfEmployed, placeOfBusiness } = {}
) => {
    const invalidFieldNames = []

    const isBusinessAddressRequired = isSelfEmployed && placeOfBusiness === PLACE_OF_BUSINESS_BUSINESS
    const isAddressSizeRequired = isSelfEmployed && placeOfBusiness === PLACE_OF_BUSINESS_HOME

    const {
        autonomousRegionId: addressAutonomousRegionId,
        city: addressCity,
        countryId: addressCountryId,
        postcode: addressPostcode,
        streetAddress: addressStreetAddress,
        addressSizeSquareMeters,
        addressUsagePercentageRange,
    } = address

    const isAddressSpain = Number(addressCountryId) === countrySpainId

    if (isAddressSpain && !addressAutonomousRegionId) invalidFieldNames.push('addressAutonomousRegionId')
    if (!addressCity) invalidFieldNames.push('addressCity')
    if (!addressCountryId) invalidFieldNames.push('addressCountryId')
    if (!addressPostcode) invalidFieldNames.push('addressPostcode')
    if (!addressStreetAddress) invalidFieldNames.push('addressStreetAddress')
    if (isAddressSizeRequired && !addressSizeSquareMeters) invalidFieldNames.push('addressSizeSquareMeters')
    if (isAddressSizeRequired && !addressUsagePercentageRange) invalidFieldNames.push('addressUsagePercentageRange')

    if (isBusinessAddressRequired) {
        const {
            autonomousRegionId: businessAddressAutonomousRegionId,
            city: businessAddressCity,
            countryId: businessAddressCountryId,
            addressOwnership: businessAddressOwnership,
            postcode: businessAddressPostcode,
            streetAddress: businessAddressStreetAddress,
            addressSizeSquareMeters: businessAddressSizeSquareMeters,
        } = businessAddress

        const isBusinessAddressSpain = Number(businessAddressCountryId) === countrySpainId

        if (isBusinessAddressSpain && !businessAddressAutonomousRegionId)
            invalidFieldNames.push('businessAddressAutonomousRegionId')
        if (!businessAddressCity) invalidFieldNames.push('businessAddressCity')
        if (!businessAddressCountryId) invalidFieldNames.push('businessAddressCountryId')
        if (!businessAddressOwnership) invalidFieldNames.push('businessAddressOwnership')
        if (!businessAddressPostcode) invalidFieldNames.push('businessAddressPostcode')
        if (!businessAddressStreetAddress) invalidFieldNames.push('businessAddressStreetAddress')
        if (!businessAddressSizeSquareMeters) invalidFieldNames.push('businessAddressSizeSquareMeters')
    }

    if (isSelfEmployed && !placeOfBusiness) invalidFieldNames.push('placeOfBusiness')

    return invalidFieldNames
}

export const getIsForalAutonomousRegion = (...regionIds) =>
    regionIds.reduce(
        (isForalAutonomousRegion, regionId) =>
            isForalAutonomousRegion ||
            (!featureFlags.featureEnableCanaryIsland && FORAL_AUTONOMOUS_REGIONS_IDS.includes(+regionId)) ||
            (featureFlags.featureEnableCanaryIsland && FORAL_AUTONOMOUS_REGIONS_IDS_WITHOUT_CANARY.includes(+regionId)),
        false
    )
